import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Container,
  Editable,
  EditablePreview,
  EditableTextarea,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useEditableControls,
  useToast,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { useMemo } from "react";
import { HiCheck, HiPencil, HiPlus } from "react-icons/hi";
import { useMutation, useQueryClient } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";

import { HTTPError, useKy } from "src/common/ky";
import { Loading } from "src/common/Loading";
import { CellKit } from "src/common/types";
import { noop } from "src/common/util";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderTabs,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { TabLink } from "src/layout/TabLink";
import {
  CELL_KITS_QUERY_KEY,
  getCellKitQueryKey,
} from "src/routes/ProductUnits/CellKits/queries";

type EditCellKitPayload = {
  notes: string;
};

function EditableControls() {
  const {
    isEditing,
    getSubmitButtonProps,
    getCancelButtonProps,
    getEditButtonProps,
  } = useEditableControls();

  return isEditing ? (
    <ButtonGroup justifyContent="center" size="sm">
      <IconButton
        aria-label="Save Changes to Notes"
        icon={<HiCheck />}
        {...getSubmitButtonProps()}
      />
      <IconButton
        aria-label="Cancel"
        icon={<HiPlus style={{ rotate: "45deg" }} />}
        {...getCancelButtonProps()}
      />
    </ButtonGroup>
  ) : (
    <Flex justifyContent="flex-end">
      <Button
        aria-label="Edit Notes"
        size="xs"
        leftIcon={<HiPencil />}
        {...getEditButtonProps()}
      >
        Edit Notes
      </Button>
    </Flex>
  );
}

const useActivateCellKitModal = (cellKitAssignmentId?: string) => {
  const { id } = useParams() as { id: string };
  const queryClient = useQueryClient();
  const ky = useKy();
  const toast = useToast();
  const activateCellKit = useMutation(
    () => ky.post(`cell-kit-assignments/${cellKitAssignmentId}/activation`),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getCellKitQueryKey(id));
        onCloseActivateModal();
        toast({
          title: "Cell Kit Activated",
          description: "The cell kit has been activated successfully.",
          status: "success",
          isClosable: true,
        });
      },
    }
  );
  const {
    isOpen: activateModalIsOpen,
    onOpen: onOpenActivateModal,
    onClose: onCloseActivateModal,
  } = useDisclosure();

  return {
    activateCellKit,
    activateModalIsOpen,
    onOpenActivateModal,
    onCloseActivateModal,
  };
};

export const CellKitDetails = () => {
  const { id } = useParams() as { id: string };
  const toast = useToast();
  const queryClient = useQueryClient();
  const cellKitQueryKey = getCellKitQueryKey(id);
  const ky = useKy();

  const data = queryClient.getQueryData<CellKit>(cellKitQueryKey);
  const latestAssignment = useMemo(() => {
    if (!data || !data.assignments.length) return null;

    return data.assignments.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })[0];
  }, [data]);
  const isPendingActivation = latestAssignment && !latestAssignment.activatedAt;

  const {
    activateCellKit,
    activateModalIsOpen,
    onOpenActivateModal,
    onCloseActivateModal,
  } = useActivateCellKitModal(latestAssignment?.cellKitAssignmentId);

  const updateCellKitNotes = useMutation<void, HTTPError, EditCellKitPayload>(
    async (values: EditCellKitPayload) => {
      await ky.patch(`cell-kits/${id}`, {
        json: values,
      });
    },
    {
      onSuccess: async () => {
        toast({
          description: "Notes updated successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        await queryClient.invalidateQueries(cellKitQueryKey);
        await queryClient.invalidateQueries(CELL_KITS_QUERY_KEY);
      },
    }
  );

  if (!data) return <Loading />;

  return (
    <>
      <PageHeader
        buttons={
          <ButtonGroup>
            {isPendingActivation ? (
              <Button colorScheme="brand.blue" onClick={onOpenActivateModal}>
                Activate Cell Kit
              </Button>
            ) : null}
            {data.assignedToIntercomId ? (
              <Link
                as={RouterLink}
                to={`/product-units/intercoms/${data.assignedToIntercomId}/details`}
              >
                <Button colorScheme="brand.blue">
                  Access Intercom Details
                </Button>
              </Link>
            ) : null}
          </ButtonGroup>
        }
      >
        <PageHeaderBackLink>
          <Link as={RouterLink} to={"/product-units/cell-kits"}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>{`${data.serialNumber} - Cell Kit Details`}</PageHeaderTitle>
        <PageHeaderTabs>
          <TabLink
            key="details"
            to={`/product-units/cell-kits/${id}/details`}
            label="Cell Kit Details"
            end
          />
          <TabLink
            key="financials"
            to={`/product-units/cell-kits/${id}/assignment-logs`}
            label="Assignment Logs"
            end
          />
        </PageHeaderTabs>
      </PageHeader>

      {isPendingActivation ? (
        <Container maxW="7xl">
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              This cell kit is pending activation. Click &quot;Activate Cell
              Kit&quot; above to activate it now.
            </AlertDescription>
          </Alert>
        </Container>
      ) : null}

      <PageContent>
        <Grid templateColumns={"1fr 2fr"} gap={4}>
          <GridItem>
            <Text>Serial Number</Text>
          </GridItem>
          <GridItem>
            <Text fontWeight={600}>{data.serialNumber}</Text>
          </GridItem>

          <GridItem>
            <Text>SIM Number</Text>
          </GridItem>
          <GridItem>
            <Text fontWeight={600}>{data.simNumber}</Text>
          </GridItem>

          <GridItem>
            <Text>Modem ID</Text>
          </GridItem>
          <GridItem>
            <Text fontWeight={600}>{data.modemId}</Text>
          </GridItem>

          <GridItem>
            <Text>Carrier</Text>
          </GridItem>
          <GridItem>
            <Text fontWeight={600}>{data.carrier || "-"}</Text>
          </GridItem>

          <GridItem>
            <Text>Activation Date</Text>
          </GridItem>
          <GridItem>
            <Text fontWeight={600}>
              {latestAssignment?.activatedAt
                ? format(new Date(latestAssignment.activatedAt), "P")
                : "-"}
            </Text>
          </GridItem>

          <GridItem>
            <Text>Activator</Text>
          </GridItem>
          <GridItem>
            <Text fontWeight={600}>
              {latestAssignment?.activatedByGatehawkUser
                ? `${latestAssignment.activatedByGatehawkUser.user.firstName} ${latestAssignment.activatedByGatehawkUser.user.lastName}`
                : "-"}
            </Text>
          </GridItem>

          <GridItem>
            <Text>Notes</Text>
          </GridItem>
          <GridItem>
            <Editable
              alignItems="center"
              defaultValue={data.notes || ""}
              display="flex"
              justifyContent="space-between"
              isPreviewFocusable={false}
              onSubmit={(value) =>
                updateCellKitNotes.mutateAsync({ notes: value }).catch(noop)
              }
              submitOnBlur={false}
              whiteSpace="pre-wrap"
            >
              <EditablePreview fontWeight={600} />
              <Box flex={1}>
                <EditableTextarea />
                <EditableControls />
              </Box>
            </Editable>
          </GridItem>
        </Grid>

        <Modal isOpen={activateModalIsOpen} onClose={onCloseActivateModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Activate Cell Kit</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              Are you sure? You can&apos;t undo this action afterwards.
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="gray" mr={3} onClick={onCloseActivateModal}>
                Close
              </Button>
              <Button
                colorScheme="brand.blue"
                onClick={() => activateCellKit.mutateAsync().catch(noop)}
                isDisabled={activateCellKit.isLoading}
              >
                Activate
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </PageContent>
    </>
  );
};
